<section class="tpr-page">
  @if (isArchived()) {
    <app-projekt-archiviert-hinweis class="tpr-archiviert-hinweis" />
  }
  @if (loading()) {
    <p class="tpr-hint" role="status">{{ 'testPlans.report.loading' | translate }}</p>
  } @else if (loadError() || !plan()) {
    <p class="tpr-hint tpr-hint--error" role="alert">{{ 'testPlans.report.loadError' | translate }}</p>
  } @else {
    <div class="tpr-header">
      <div class="tpr-header__left">
        <div class="tpr-header__title-row">
          <button
            type="button"
            class="tpr-header__back"
            [attr.aria-label]="'testPlans.report.back' | translate"
            (click)="onBack()"
          >
            <mat-icon fontSet="material-icons-outlined" aria-hidden="true">chevron_left</mat-icon>
          </button>
          <h1 class="tpr-header__title">{{ 'testPlans.report.headline' | translate }}</h1>
          <code class="tpr-header__id">{{ plan()!.publicId }}</code>
          <app-plan-status-pill [value]="plan()!.status" />
        </div>
        <div class="tpr-header__meta">{{ plan()!.title }} · {{ plan()!.releaseLabel ?? '—' }}</div>
      </div>
      <div class="tpr-header__actions">
        @if (!isReadOnly()) {
          <button type="button" class="tpr-header__action tpr-header__edit" (click)="onOpenPlan()">
            <mat-icon fontSet="material-icons-outlined" aria-hidden="true">edit</mat-icon>
            {{ 'testPlans.report.editPlan' | translate }}
          </button>
        }
        <button
          type="button"
          class="tpr-header__action"
          data-testid="plan-report-copy-filter"
          [attr.aria-label]="'testPlans.report.copyFilter' | translate"
          (click)="onCopyFilter()"
        >
          <span aria-hidden="true">⧉</span>
          {{ 'testPlans.report.copyFilter' | translate }}
        </button>
        @if (plan()!.releaseId) {
          <button
            type="button"
            class="tpr-header__action"
            data-testid="plan-report-pdf"
            [attr.aria-label]="
              (pdfBusy() ? 'testPlans.report.exportPdfBusy' : 'testPlans.report.exportPdf') | translate
            "
            [disabled]="pdfBusy()"
            (click)="onExportPdf()"
          >
            @if (pdfBusy()) {
              <mat-progress-spinner mode="indeterminate" diameter="14" aria-hidden="true" />
              {{ 'testPlans.report.exportPdfBusy' | translate }}
            } @else {
              <span aria-hidden="true">↗</span>
              {{ 'testPlans.report.exportPdf' | translate }}
            }
          </button>
        }
        @if (canStart()) {
          <button
            type="button"
            class="tpr-header__action tpr-header__action--primary"
            data-testid="plan-report-start"
            (click)="onStart()"
          >
            <span aria-hidden="true">▶</span>
            {{ startLabelKey() | translate }}
          </button>
        }
        <span class="sr-only" role="status">{{ pdfBusy() ? ('testPlans.report.exportPdfBusy' | translate) : '' }}</span>
      </div>
    </div>

    <div class="tpr-filter-card">
      <div class="tpr-filter-card__grid">
        <div class="tpr-filter">
          <div class="tpr-filter__label">{{ 'testPlans.report.filter.plan' | translate }}</div>
          <div class="tpr-filter__pillbox">
            <span class="tpr-filter__pill">{{ plan()!.publicId }} · {{ plan()!.releaseLabel ?? '—' }}</span>
          </div>
        </div>
        <div class="tpr-filter">
          <div class="tpr-filter__label">{{ 'testPlans.report.filter.testCase' | translate }}</div>
          <div class="tpr-filter__search">
            <mat-icon fontSet="material-icons-outlined" aria-hidden="true">search</mat-icon>
            <input
              type="text"
              [value]="search()"
              (input)="onSearchInput($event)"
              [placeholder]="'testPlans.report.filter.searchPlaceholder' | translate"
              [attr.aria-label]="'testPlans.report.filter.searchAria' | translate"
            />
          </div>
        </div>
        <div class="tpr-filter">
          <div class="tpr-filter__label">{{ 'testPlans.report.filter.basis' | translate }}</div>
          <mat-radio-group
            class="tpr-basis"
            data-testid="plan-report-basis"
            color="primary"
            [attr.aria-label]="'testPlans.report.filter.basis' | translate"
            [value]="basis()"
            (change)="onBasisChange($event.value)"
          >
            <mat-radio-button value="status" data-testid="plan-report-basis-status">
              {{ 'testPlans.report.filter.basisStatus' | translate }}
            </mat-radio-button>
            <mat-radio-button
              value="schritte"
              data-testid="plan-report-basis-schritte"
              [disabled]="details() === null"
              [matTooltip]="'testPlans.report.stepsUnavailable' | translate"
              [matTooltipDisabled]="!detailsFailed()"
            >
              {{ 'testPlans.report.filter.basisSteps' | translate }}
            </mat-radio-button>
          </mat-radio-group>
        </div>
      </div>
    </div>

    <h2 class="tpr-section-title">{{ 'testPlans.report.summary' | translate }}</h2>
    <div class="tpr-summary" role="group" [attr.aria-label]="'testPlans.report.summary' | translate">
      <div class="tpr-summary__item tpr-summary__item--plans">
        <span class="tpr-summary__value">1</span>
        <span class="tpr-summary__label">{{ 'testPlans.report.summaryLabels.testPlans' | translate }}</span>
      </div>
      <div class="tpr-summary__item tpr-summary__item--total">
        <span class="tpr-summary__value">{{ totalCount() }}</span>
        <span class="tpr-summary__label">
          {{
            (countsSteps() ? 'testPlans.report.summaryLabels.steps' : 'testPlans.report.summaryLabels.testCases')
              | translate
          }}
        </span>
      </div>
      <div class="tpr-summary__item tpr-summary__item--passed">
        <span class="tpr-summary__value">{{ passedCount() }}</span>
        <span class="tpr-summary__label">{{ 'testPlans.report.status.passed' | translate }}</span>
      </div>
      <div class="tpr-summary__item tpr-summary__item--failed">
        <span class="tpr-summary__value">{{ failedCount() }}</span>
        <span class="tpr-summary__label">{{ 'testPlans.report.status.failed' | translate }}</span>
      </div>
      <div class="tpr-summary__item tpr-summary__item--in-progress">
        <span class="tpr-summary__value">{{ inProgressCount() }}</span>
        <span class="tpr-summary__label">{{ 'testPlans.report.status.inProgress' | translate }}</span>
      </div>
      <div class="tpr-summary__item tpr-summary__item--open">
        <span class="tpr-summary__value">{{ openCount() }}</span>
        <span class="tpr-summary__label">{{ 'testPlans.report.status.open' | translate }}</span>
      </div>
    </div>

    <div class="tpr-progress-heading">
      {{ 'testPlans.report.progress' | translate }}
      <span class="tpr-progress-heading__percent">
        – {{ completedPercent() }}% {{ 'testPlans.report.completed' | translate }}
      </span>
    </div>
    <div class="tpr-progress-bar" role="img" [attr.aria-label]="'testPlans.report.progress' | translate">
      @for (seg of segments(); track seg.kind) {
        <div class="tpr-progress-bar__seg" [class]="'tpr-progress-bar__seg--' + seg.kind" [style.width.%]="seg.percent">
          {{ seg.percent }}%
        </div>
      }
    </div>
    <div class="tpr-legend">
      <span class="tpr-legend__item tpr-legend__item--passed">
        <span class="tpr-legend__dot" aria-hidden="true"></span>
        {{ 'testPlans.report.status.passed' | translate }}
      </span>
      <span class="tpr-legend__item tpr-legend__item--failed">
        <span class="tpr-legend__dot" aria-hidden="true"></span>
        {{ 'testPlans.report.status.failed' | translate }}
      </span>
      <span class="tpr-legend__item tpr-legend__item--in-progress">
        <span class="tpr-legend__dot" aria-hidden="true"></span>
        {{ 'testPlans.report.status.inProgress' | translate }}
      </span>
      <span class="tpr-legend__item tpr-legend__item--open">
        <span class="tpr-legend__dot" aria-hidden="true"></span>
        {{ 'testPlans.report.status.open' | translate }}
      </span>
    </div>

    <div class="tpr-table" role="table" [attr.aria-label]="'testPlans.report.tableAria' | translate">
      <div class="tpr-table__head" role="row">
        <div class="tpr-table__th tpr-table__th--plan" role="columnheader">
          {{ 'testPlans.report.columns.plan' | translate }}
        </div>
        <div class="tpr-table__th tpr-table__th--tc" role="columnheader">
          {{ 'testPlans.report.columns.testCase' | translate }}
        </div>
        <div class="tpr-table__th tpr-table__th--summary" role="columnheader">
          {{ 'testPlans.report.columns.summary' | translate }}
        </div>
        <div class="tpr-table__th tpr-table__th--status" role="columnheader">
          {{ 'testPlans.report.columns.status' | translate }}
        </div>
        <div class="tpr-table__th tpr-table__th--req" role="columnheader">
          {{ 'testPlans.report.columns.requirement' | translate }}
        </div>
        <div class="tpr-table__th tpr-table__th--defects" role="columnheader">
          {{ 'testPlans.report.columns.defects' | translate }}
        </div>
      </div>
      @for (row of filteredRows(); track row.testCaseId) {
        <div
          class="tpr-table__row"
          data-testid="plan-report-row"
          role="row"
          tabindex="0"
          (click)="onOpenResults(row)"
          (keydown.enter)="onOpenResults(row)"
        >
          <div class="tpr-table__td tpr-table__td--plan" role="cell">
            <a
              class="tpr-table__link"
              data-testid="plan-report-plan-link"
              queryParamsHandling="preserve"
              [routerLink]="['/projects', projectId, 'test-plans', row.planId, 'report']"
              (click)="$event.stopPropagation()"
              (keydown.enter)="$event.stopPropagation()"
            >
              {{ row.planPublicId }}
            </a>
          </div>
          <div class="tpr-table__td tpr-table__td--tc" role="cell">
            <a
              class="tpr-table__link"
              data-testid="plan-report-tc-link"
              [routerLink]="['/projects', projectId, 'test-cases', row.testCaseId, 'edit']"
              (click)="$event.stopPropagation()"
              (keydown.enter)="$event.stopPropagation()"
            >
              {{ row.testCaseKey }}
            </a>
          </div>
          <div class="tpr-table__td tpr-table__td--summary" role="cell">{{ row.summary }}</div>
          <div class="tpr-table__td tpr-table__td--status" role="cell">
            <app-result-status-badge [kind]="row.status" />
          </div>
          <div class="tpr-table__td tpr-table__td--req" role="cell">
            @if (row.requirementLabel) {
              <span class="tpr-table__req-check" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
              @if (row.requirementId) {
                <a
                  class="tpr-table__link"
                  data-testid="plan-report-req-link"
                  [routerLink]="['/projects', projectId, 'requirements', row.requirementId, 'edit']"
                  (click)="$event.stopPropagation()"
                  (keydown.enter)="$event.stopPropagation()"
                >
                  {{ row.requirementLabel }}
                </a>
              } @else {
                <code class="tpr-table__link">{{ row.requirementLabel }}</code>
              }
            } @else {
              <span class="tpr-table__faint">—</span>
            }
          </div>
          <div
            class="tpr-table__td tpr-table__td--defects"
            [class.tpr-table__td--defects-found]="(row.defects ?? 0) > 0"
            data-testid="plan-report-defects"
            role="cell"
          >
            {{ row.defects ?? '—' }}
          </div>
        </div>
      } @empty {
        <p class="tpr-table__empty">
          {{ (rows().length ? 'testPlans.report.emptySearch' : 'testPlans.report.emptyRows') | translate }}
        </p>
      }
    </div>
  }
</section>
